import { API_URL } from '@/utils/constants'

/**
 * A laid-out block: packed [x, y, size, index] per transaction in grid units.
 * Shared between the WebGL and Canvas 2D renderers.
 */
export type BitmapLayout = {
	instances: Float32Array
	count: number
	layoutWidth: number
	usedHeight: number
	blockTime: number | null
}

export class BitmapNotFoundError extends Error {
	constructor(height: number) {
		super(`Block ${height} is not available yet`)
		this.name = 'BitmapNotFoundError'
	}
}

const MAX_CACHED = 300
const cache = new Map<number, BitmapLayout>()
const inflight = new Map<number, Promise<BitmapLayout>>()

let worker: Worker | null = null
let nextId = 1
const pending = new Map<
	number,
	{ resolve: (l: Omit<BitmapLayout, 'blockTime'>) => void; reject: (e: Error) => void }
>()

function getWorker(): Worker {
	if (worker) return worker
	worker = new Worker('/bitmap-worker.js')
	worker.onmessage = (e: MessageEvent) => {
		const { type, id } = e.data || {}
		const p = pending.get(id)
		if (!p) return
		pending.delete(id)
		if (type === 'done') {
			p.resolve({
				instances: e.data.instances as Float32Array,
				count: e.data.count as number,
				layoutWidth: e.data.layoutWidth as number,
				usedHeight: e.data.usedHeight as number
			})
		} else {
			p.reject(new Error(e.data?.message || 'layout failed'))
		}
	}
	worker.onerror = () => {
		for (const p of pending.values()) p.reject(new Error('bitmap worker crashed'))
		pending.clear()
		worker?.terminate()
		worker = null
	}
	return worker
}

function layoutInWorker(buffer: ArrayBuffer): Promise<Omit<BitmapLayout, 'blockTime'>> {
	return new Promise((resolve, reject) => {
		const id = nextId++
		pending.set(id, { resolve, reject })
		getWorker().postMessage({ type: 'layout', id, buffer }, [buffer])
	})
}

function remember(height: number, layout: BitmapLayout) {
	if (cache.size >= MAX_CACHED) {
		const oldest = cache.keys().next().value
		if (oldest !== undefined) cache.delete(oldest)
	}
	cache.set(height, layout)
}

export function peekBitmapLayout(height: number): BitmapLayout | undefined {
	return cache.get(height)
}

/**
 * Fetch the block's per-transaction size bytes from our API and lay them out
 * off the main thread. Results are memoised per height (the block never
 * changes) and concurrent callers share one request.
 */
export function loadBitmapLayout(height: number): Promise<BitmapLayout> {
	const hit = cache.get(height)
	if (hit) return Promise.resolve(hit)
	const running = inflight.get(height)
	if (running) return running

	const task = (async () => {
		const res = await fetch(`${API_URL}/bitmap/block/${height}`)
		if (res.status === 404) throw new BitmapNotFoundError(height)
		if (!res.ok) throw new Error(`HTTP ${res.status}`)
		const blockTimeHeader = res.headers.get('x-block-time')
		const blockTime = blockTimeHeader ? Number(blockTimeHeader) || null : null
		const buffer = await res.arrayBuffer()
		const laid = await layoutInWorker(buffer)
		const layout: BitmapLayout = { ...laid, blockTime }
		remember(height, layout)
		return layout
	})()

	inflight.set(height, task)
	task.finally(() => inflight.delete(height)).catch(() => {})
	return task
}
